import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = [["meus", "Os meus", "mine"], ["equipa", "Equipa", "team"], ["fila", "Sem gestor", "queue"], ["concluidos", "Concluídos", "concluidos"]];
const PHASES = ["Proposta aceite", "CPCV marcado", "CPCV assinado", "Escritura marcada"];
const STATES = [["pronta", "Pronta"], ["falta", "Falta"], ["em", "Em risco"], ["bloqueada", "Bloqueada"]];
const CHIP = { green: "bg-green-50 text-green-700", amber: "bg-amber-50 text-amber-800", red: "bg-red-50 text-red-700", grey: "bg-slate-100 text-slate-600" };

function Chip({ r }) {
  return (
    <div className="min-w-0">
      <span className={`inline-block text-sm font-semibold px-2.5 py-0.5 rounded-md ${CHIP[r.tone]}`} data-testid={`gp-state-${r.ref}`}>{r.state}</span>
      {r.why && <div className="text-sm text-slate-500 mt-1 truncate" title={r.why}>{r.why}</div>}
    </div>
  );
}

function When({ r }) {
  if (!r.date_label) return <span className="text-sm text-slate-400">{r.date_sub || "—"}</span>;
  return (
    <div data-testid={`gp-date-${r.ref}`}>
      <div className={`text-[15px] font-semibold ${r.date_red ? "text-red-700" : "text-slate-800"}`}>{r.date_label}</div>
      <div className="text-sm text-slate-500">{r.date_sub}</div>
    </div>
  );
}

function Title({ r }) {
  return (
    <div className="min-w-0">
      <div className="text-[15px] font-semibold text-slate-900">{r.title}{r.ensaio && <span className="ml-2 align-middle text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700" data-testid={`gp-row-ensaio-${r.ref}`}>Ensaio</span>}{r.novo && <span className="ml-2 align-middle text-[11px] font-bold tracking-wider text-green-700 bg-green-50 rounded px-1.5 py-0.5">NOVO</span>}</div>
      {r.sub && <div className="text-sm text-slate-500">{r.sub}</div>}
    </div>
  );
}

function FicoBtn({ r, onDone, mobile = false }) {
  const [busy, setBusy] = useState(false);
  const fico = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await api.post(`/gestao/processes/${r.pid}/fico`); toast.success(`${r.title.split(" · ").pop()} ficou contigo`); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Não foi possível."); setBusy(false); }
  };
  return <button onClick={fico} disabled={busy} className="h-10 px-4 rounded-[12px] navy-bg text-white text-sm font-semibold whitespace-nowrap hover:brightness-110 transition-[filter] disabled:opacity-60" data-testid={`gp-fico-${mobile ? "m-" : ""}${r.ref}`}>Fico com este</button>;
}

function Row({ r, team, fila, onOpen, onDone }) {
  return (
    <tr onClick={() => onOpen(r)} className={`border-t border-slate-100 cursor-pointer transition-colors hover:bg-slate-50 ${r.novo ? "bg-green-50/40" : ""}`} data-testid={`gp-row-${r.ref}`}>
      <td className="py-4 pl-6 pr-4"><Title r={r} /></td>
      <td className="py-4 pr-4 text-[15px] text-slate-700 whitespace-nowrap">{r.consultant}</td>
      {team && <td className="py-4 pr-4 text-[15px] text-slate-700 whitespace-nowrap" data-testid={`gp-gestor-${r.ref}`}>{r.gestor || <span className="text-slate-400">sem gestor</span>}</td>}
      <td className="py-4 pr-4 text-[15px] font-medium text-slate-800 whitespace-nowrap">{r.phase_label}</td>
      <td className="py-4 pr-4 whitespace-nowrap"><When r={r} /></td>
      <td className="py-4 pr-4 max-w-[260px]"><Chip r={r} /></td>
      <td className={`py-4 pr-6 text-center text-[15px] font-semibold ${r.n ? "text-slate-900" : "text-slate-400"}`} data-testid={`gp-n-${r.ref}`}>{r.n}</td>
      {fila && <td className="py-4 pr-6 text-right"><FicoBtn r={r} onDone={onDone} /></td>}
    </tr>
  );
}

function MobileCard({ r, fila, onOpen, onDone }) {
  return (
    <li onClick={() => onOpen(r)} className="bg-white rounded-[16px] border border-slate-100 p-4 shadow-[0_4px_18px_rgb(0,0,0,0.04)] cursor-pointer" data-testid={`gp-card-${r.ref}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-[15px] font-semibold text-slate-900 min-w-0">{r.title}</div>
        <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-md ${CHIP[r.tone]}`}>{r.state}</span>
      </div>
      <div className={`mt-1 text-sm ${r.date_red ? "text-red-700 font-semibold" : "text-slate-500"}`}>{[r.date_label, r.date_sub].filter(Boolean).join(" · ")}</div>
      {fila && <div className="mt-3"><FicoBtn r={r} onDone={onDone} mobile /></div>}
    </li>
  );
}

function Filter({ value, onChange, label, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-40 h-10 bg-white border-slate-200" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{label}</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function useGp(scope) {
  const [d, setD] = useState(null);
  const load = () => { api.get("/gestao/processos", { params: { scope } }).then(({ data }) => setD(data)); };
  useEffect(() => { setD(null); load(); }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps
  return [d, load];
}

export function GestaoProcessos({ isDirecao = false }) {
  const navigate = useNavigate();
  const [scope, setScope] = useState(isDirecao ? "equipa" : "meus");
  const [d, load] = useGp(scope);
  const [q, setQ] = useState("");
  const [fase, setFase] = useState("all");
  const [estado, setEstado] = useState("all");
  const [cons, setCons] = useState("all");
  const tabs = TABS.filter(([k]) => !(isDirecao && k === "meus"));
  const rows = d?.rows || [];
  const consultores = [...new Map(rows.filter((r) => r.consultant_id).map((r) => [r.consultant_id, r.consultant])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const s = q.trim().toLowerCase();
  const shown = rows.filter((r) => (fase === "all" || r.phase_label === fase) && (estado === "all" || r.state_key === estado)
    && (cons === "all" || r.consultant_id === cons) && (!s || [r.title, r.sub, r.consultant, r.gestor, r.ref].some((x) => (x || "").toLowerCase().includes(s))));
  const team = scope !== "meus";
  const fila = scope === "fila" && d?.can_fico;
  const open = (r) => navigate(`/processo/${r.pid}`);
  return (
    <div className="space-y-6" data-testid="gp-processos">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl text-ec-green">Processos</h1>
        <p className="text-slate-500 mt-1" data-testid="gp-subtitle">Vista da gestão processual{d?.name && !isDirecao ? ` · ${d.name.split(" ")[0]}` : ""}</p>
      </div>
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
        <div className="grid grid-cols-2 sm:flex gap-1 bg-white rounded-xl p-1 border border-slate-100 w-full sm:w-fit" data-testid="gp-tabs">
          {tabs.map(([k, l, c]) => (
            <button key={k} onClick={() => setScope(k)} data-testid={`gp-tab-${k}`}
              className={`px-4 py-2 rounded-lg text-[15px] whitespace-nowrap transition-colors ${scope === k ? "navy-bg text-white font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
              {l}{d?.counts && <span className={`ml-1.5 text-sm ${scope === k ? "text-white/70" : "text-slate-400"}`} data-testid={`gp-count-${k}`}>{d.counts[c]}</span>}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 sm:flex gap-2">
          <div className="relative col-span-3 sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar morada, parte…" className="pl-9 h-10 bg-white border-slate-200" data-testid="gp-search" />
          </div>
          {scope !== "concluidos" && <Filter value={fase} onChange={setFase} label="Fase" options={PHASES.map((p) => [p, p])} testid="gp-filter-fase" />}
          {scope !== "concluidos" && <Filter value={estado} onChange={setEstado} label="Estado" options={STATES} testid="gp-filter-estado" />}
          <Filter value={cons} onChange={setCons} label="Consultor" options={consultores} testid="gp-filter-consultor" />
        </div>
      </div>
      <GpList d={d} shown={shown} team={team} fila={fila} open={open} load={load} scope={scope} />
    </div>
  );
}

function GpList({ d, shown, team, fila, open, load, scope }) {
  if (!d) return <div className="text-slate-400 py-20 text-center">A carregar…</div>;
  if (!shown.length) return <div className="bg-white rounded-2xl p-10 text-center border border-slate-100 text-slate-500" data-testid="gp-empty">{scope === "fila" ? "Não há processos sem gestor." : "Nenhum processo."}</div>;
  return (
    <>
      <div className="hidden md:block bg-white rounded-[18px] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-x-auto" data-testid="gp-table">
        <table className="w-full text-left">
          <thead>
            <tr className="text-xs font-bold tracking-[0.12em] uppercase text-slate-500">
              <th className="py-4 pl-6 pr-4">Processo</th><th className="py-4 pr-4">Consultor</th>{team && <th className="py-4 pr-4">Gestor</th>}
              <th className="py-4 pr-4">Fase</th><th className="py-4 pr-4">{scope === "concluidos" ? "Data" : "Próxima data ↑"}</th><th className="py-4 pr-4">Estado</th>
              <th className="py-4 pr-6 text-center">A tratar</th>{fila && <th className="py-4 pr-6" />}
            </tr>
          </thead>
          <tbody>{shown.map((r) => <Row key={r.pid} r={r} team={team} fila={fila} onOpen={open} onDone={load} />)}</tbody>
        </table>
      </div>
      <ul className="md:hidden space-y-3" data-testid="gp-cards">{shown.map((r) => <MobileCard key={r.pid} r={r} fila={fila} onOpen={open} onDone={load} />)}</ul>
      {scope !== "concluidos" && <p className="text-sm text-slate-500" data-testid="gp-note">Ordenado pela próxima data (CPCV ou escritura). Vermelho: 4 dias ou menos e com problema. «A tratar» é o mesmo número do processo (todas as tarefas). Clicar numa linha abre o processo.</p>}
    </>
  );
}
